import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ArtistLayout from '../components/layout/ArtistLayout';
import { 
  Sparkles, 
  Trophy,
  Lightbulb,
  BookOpen,
  Target,
  CheckCircle,
  Clock,
  Calendar,
  Plus,
  X,
  Send,
  Edit,
  AlertCircle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  milestone: { icon: Target, label: 'Milestone', emoji: '🎯', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300' },
  innovation: { icon: Lightbulb, label: 'Innovation', emoji: '💡', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' },
  award: { icon: Trophy, label: 'Award', emoji: '🏆', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' },
  learning: { icon: BookOpen, label: 'Learning', emoji: '📚', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300' },
  other: { icon: Sparkles, label: 'Achievement', emoji: '⭐', color: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' }
};

const ArtistAchievements = () => {
  const { isDark } = useTheme();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'milestone',
    impact: '',
    date_achieved: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/artist/achievements');
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        // Check if resubmitting (needs_revision status)
        const achievement = achievements.find(a => a.id === editingId);
        if (achievement?.status === 'needs_revision') {
          await api.patch(`/achievements/${editingId}/resubmit`, formData);
          toast.success('Achievement resubmitted for review!');
        } else {
          await api.put(`/achievements/${editingId}`, formData);
          toast.success('Achievement updated!');
        }
      } else {
        await api.post('/achievements', formData);
        toast.success('Achievement submitted for approval!');
      }
      resetForm();
      fetchAchievements();
    } catch (error) {
      console.error('Error submitting achievement:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit achievement');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'milestone',
      impact: '',
      date_achieved: new Date().toISOString().split('T')[0]
    });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (achievement) => {
    setFormData({
      title: achievement.title || '',
      description: achievement.description || '',
      category: achievement.category || 'milestone',
      impact: achievement.impact || '',
      date_achieved: achievement.date_achieved || new Date().toISOString().split('T')[0]
    });
    setEditingId(achievement.id);
    setShowForm(true);
  };

  const filteredAchievements = achievements.filter(a => {
    if (filter === 'all') return true;
    return a.category === filter;
  });

  const stats = {
    total: achievements.length,
    approved: achievements.filter(a => a.status === 'approved').length,
    pending: achievements.filter(a => a.status === 'pending').length,
    needsRevision: achievements.filter(a => a.status === 'needs_revision').length
  };

  return (
    <ArtistLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="artist-achievements">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={cn("text-2xl font-bold font-outfit flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
              <Sparkles className="w-7 h-7 text-[#331062]" />
              My Achievements
            </h1>
            <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
              Your milestones, innovations, and recognitions
            </p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            data-testid="add-achievement-btn"
            className="bg-[#331062] hover:bg-[#4a1d8a] text-white flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Achievement
          </Button>
        </div>

        {/* Submit Achievement Modal/Form */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className={cn("p-6", isDark ? "bg-slate-800 border-slate-700" : "border-2 border-[#331062]/20")}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className={cn("text-lg font-semibold flex items-center gap-2", isDark ? "text-white" : "text-slate-800")}>
                    <Send className="w-5 h-5 text-[#331062]" />
                    {editingId ? 'Edit Achievement' : 'Submit New Achievement'}
                  </h2>
                  <button onClick={resetForm} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700">
                    <X className={cn("w-5 h-5", isDark ? "text-slate-400" : "text-slate-500")} />
                  </button>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="achievement-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title" className={isDark ? "text-slate-300" : ""}>Title *</Label>
                      <Input
                        id="title"
                        data-testid="achievement-title"
                        placeholder="e.g., Completed Advanced Training"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        className={isDark ? "bg-slate-700 border-slate-600 text-white" : ""}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="type" className={isDark ? "text-slate-300" : ""}>Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                        <SelectTrigger data-testid="achievement-type" className={isDark ? "bg-slate-700 border-slate-600 text-white" : ""}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="milestone">🎯 Milestone</SelectItem>
                          <SelectItem value="innovation">💡 Innovation</SelectItem>
                          <SelectItem value="award">🏆 Award</SelectItem>
                          <SelectItem value="learning">📚 Learning</SelectItem>
                          <SelectItem value="other">⭐ Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description" className={isDark ? "text-slate-300" : ""}>Description</Label>
                    <textarea
                      id="description"
                      data-testid="achievement-description"
                      placeholder="Describe your achievement in detail..."
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      rows={3}
                      className={cn(
                        "w-full px-3 py-2 rounded-md border text-sm resize-none",
                        isDark 
                          ? "bg-slate-700 border-slate-600 text-white placeholder:text-slate-400" 
                          : "border-slate-200 placeholder:text-slate-400"
                      )}
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="impact" className={isDark ? "text-slate-300" : ""}>Business Impact</Label>
                      <Input
                        id="impact"
                        data-testid="achievement-impact"
                        placeholder="e.g., Improved efficiency by 20%"
                        value={formData.impact}
                        onChange={(e) => setFormData({...formData, impact: e.target.value})}
                        className={isDark ? "bg-slate-700 border-slate-600 text-white" : ""}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date" className={isDark ? "text-slate-300" : ""}>Date Achieved</Label>
                      <Input
                        id="date"
                        type="date"
                        data-testid="achievement-date"
                        value={formData.date_achieved}
                        onChange={(e) => setFormData({...formData, date_achieved: e.target.value})}
                        className={isDark ? "bg-slate-700 border-slate-600 text-white" : ""}
                      />
                    </div>
                  </div>
                  
                  <div className={cn("p-3 rounded-lg text-sm", isDark ? "bg-slate-700/50" : "bg-blue-50")}>
                    <p className={cn("flex items-center gap-2", isDark ? "text-slate-300" : "text-blue-700")}>
                      <AlertCircle className="w-4 h-4" />
                      Your achievement will be submitted for manager approval.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={resetForm} data-testid="cancel-achievement-btn">
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      data-testid="submit-achievement-btn"
                      className="bg-[#331062] hover:bg-[#4a1d8a] text-white flex items-center gap-2"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                          Submitting...
                        </span>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {editingId ? 'Update' : 'Submit for Approval'}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={cn("p-5", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                <Trophy className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-800")}>{stats.total}</p>
                <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Total</p>
              </div>
            </div>
          </Card>
          <Card className={cn("p-5", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Approved</p>
              </div>
            </div>
          </Card>
          <Card className={cn("p-5", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50">
                <Clock className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Pending</p>
              </div>
            </div>
          </Card>
          <Card className={cn("p-5", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/50">
                <AlertCircle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.needsRevision}</p>
                <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Needs Revision</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          {['all', 'milestone', 'innovation', 'award', 'learning'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={cn(
                "px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                filter === f
                  ? "bg-[#331062] text-white"
                  : isDark
                    ? "bg-slate-700 text-slate-300 hover:bg-slate-600"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              )}
            >
              {f === 'all' ? 'All' : `${TYPE_CONFIG[f]?.emoji} ${TYPE_CONFIG[f]?.label}`}
            </button>
          ))}
        </div>

        {/* Achievements Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
          </div>
        ) : filteredAchievements.length === 0 ? (
          <Card className={cn("p-12 text-center", isDark ? "bg-slate-800 border-slate-700" : "")}>
            <Sparkles className={cn("w-16 h-16 mx-auto mb-4", isDark ? "text-slate-600" : "text-slate-300")} />
            <h3 className={cn("text-lg font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
              {filter === 'all' ? 'No achievements yet' : `No ${filter} achievements`}
            </h3>
            <p className={cn("text-sm mb-4", isDark ? "text-slate-400" : "text-slate-500")}>
              Submit your first achievement and get recognized!
            </p>
            <Button
              onClick={() => setShowForm(true)}
              data-testid="add-first-achievement-btn"
              className="bg-[#331062] hover:bg-[#4a1d8a] text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Achievement
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {filteredAchievements.map((achievement, index) => {
              const typeConfig = TYPE_CONFIG[achievement.category] || TYPE_CONFIG.other;
              const TypeIcon = typeConfig.icon;
              const canEdit = achievement.status === 'pending' || achievement.status === 'needs_revision';
              
              const getStatusBadge = () => {
                switch(achievement.status) {
                  case 'approved':
                    return { text: '✓ Approved', class: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300' };
                  case 'rejected':
                    return { text: '✗ Rejected', class: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300' };
                  case 'needs_revision':
                    return { text: '⚠ Needs Revision', class: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300' };
                  default:
                    return { text: '⏳ Pending', class: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300' };
                }
              };
              const statusBadge = getStatusBadge();
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className={cn(
                    "p-5 h-full relative",
                    isDark ? "bg-slate-800 border-slate-700" : "",
                    achievement.status === 'needs_revision' && "border-orange-300 dark:border-orange-700"
                  )}>
                    {/* Edit Button */}
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(achievement)}
                        data-testid={`edit-achievement-${achievement.id}`}
                        className={cn(
                          "absolute top-3 right-3 p-1.5 rounded-lg transition-colors",
                          isDark ? "hover:bg-slate-700 text-slate-400" : "hover:bg-slate-100 text-slate-500"
                        )}
                        title="Edit achievement"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-3xl">{typeConfig.emoji}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn("text-xs px-2 py-0.5 rounded-full", typeConfig.color)}>
                            {typeConfig.label}
                          </span>
                          <span className={cn("text-xs px-2 py-0.5 rounded-full", statusBadge.class)}>
                            {statusBadge.text}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <h3 className={cn("font-semibold text-lg mb-2 pr-8", isDark ? "text-white" : "text-slate-800")}>
                      {achievement.title}
                    </h3>
                    
                    {achievement.description && (
                      <p className={cn("text-sm mb-3", isDark ? "text-slate-400" : "text-slate-500")}>
                        {achievement.description}
                      </p>
                    )}

                    {achievement.impact && (
                      <div className={cn(
                        "p-2 rounded-lg text-sm mb-3",
                        isDark ? "bg-green-900/20 text-green-300" : "bg-green-50 text-green-700"
                      )}>
                        💪 Impact: {achievement.impact}
                      </div>
                    )}
                    
                    {/* Revision feedback if any */}
                    {achievement.status === 'needs_revision' && achievement.review_feedback && (
                      <div className={cn(
                        "p-2 rounded-lg text-sm mb-3",
                        isDark ? "bg-orange-900/20 text-orange-300" : "bg-orange-50 text-orange-700"
                      )}>
                        📝 Feedback: {achievement.review_feedback}
                      </div>
                    )}
                    
                    {/* Rejection reason if any */}
                    {achievement.status === 'rejected' && achievement.rejection_reason && (
                      <div className={cn(
                        "p-2 rounded-lg text-sm mb-3",
                        isDark ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"
                      )}>
                        ❌ Reason: {achievement.rejection_reason}
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between mt-auto pt-3 border-t border-slate-200 dark:border-slate-700">
                      {(achievement.date || achievement.date_achieved) && (
                        <span className={cn("text-xs flex items-center gap-1", isDark ? "text-slate-500" : "text-slate-400")}>
                          <Calendar className="w-3 h-3" />
                          {new Date(achievement.date || achievement.date_achieved).toLocaleDateString()}
                        </span>
                      )}
                      {achievement.approved_by && (
                        <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                          Approved by: {achievement.approved_by}
                        </span>
                      )}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </ArtistLayout>
  );
};

export default ArtistAchievements;
